import {
  CryptoAssetType,
  SLIP44,
  type CryptoAsset,
  type CryptoAssetDTO,
  type CryptoAssetRoutingKey,
  type CryptoAssetTraits,
} from '@/types/WalletTypes';

const normalizeSymbol = (symbol?: string) => symbol?.toUpperCase();

const getBitcoinSlip44CoinType = () =>
  process.env.NEXT_PUBLIC_ENVIRONMENT === 'production' ? SLIP44.BITCOIN : SLIP44.BITCOIN_TESTNET;

const INFURA_RPC_URL = /^https:\/\/([a-z0-9-]+)\.infura\.io\/v3(\/|$)/i;
const ETHERSCAN_V2_API_URL = /^https:\/\/api\.etherscan\.io\/v2\/api/i;

const isBitcoinAssetDTO = (asset: CryptoAssetDTO) =>
  asset.blockchainConfiguration.slip44CoinType === SLIP44.BITCOIN ||
  asset.blockchainConfiguration.slip44CoinType === SLIP44.BITCOIN_TESTNET;

const isEvmAssetDTO = (asset: CryptoAssetDTO) => asset.blockchainConfiguration.slip44CoinType === SLIP44.ETHEREUM;

const isNativeAssetDTO = (asset: CryptoAssetDTO) => asset.assetType.id === CryptoAssetType.Native;

const isErc20AssetDTO = (asset: CryptoAssetDTO) => asset.assetType.id === CryptoAssetType.Erc20;

const isDefaultLayer1AssetDTO = (asset: CryptoAssetDTO) => asset.blockchainConfiguration.isDefaultLayer1 === true;

const isDefaultLayer2AssetDTO = (asset: CryptoAssetDTO) => asset.blockchainConfiguration.isDefaultLayer2 === true;

const getBlockchainKey = (asset: CryptoAssetDTO) =>
  `${asset.blockchainConfiguration.slip44CoinType}:${asset.blockchainConfiguration.chainId ?? '_'}`;

const resolveProxiedRpcUrl = (asset: CryptoAssetDTO) => {
  const { rpcUrl } = asset.blockchainConfiguration;
  const usesInfuraRpcUrl = rpcUrl.match(INFURA_RPC_URL);
  if (usesInfuraRpcUrl) {
    return `/api/infura?network=${usesInfuraRpcUrl[1].toLowerCase()}`;
  }
  return rpcUrl;
};

const resolveProxiedBlockExplorerApiUrl = (asset: CryptoAssetDTO) => {
  const { blockExplorerApiUrl, chainId } = asset.blockchainConfiguration;
  const usesEtherscanApiUrl = blockExplorerApiUrl?.match(ETHERSCAN_V2_API_URL);
  if (usesEtherscanApiUrl) {
    return `/api/coins/explorerTxnHistory?chainid=${chainId}`;
  }
  return blockExplorerApiUrl;
};

const validateCryptoAssetDTO = (
  asset: CryptoAssetDTO,
  { allowMissingSlip44CoinType = false }: { allowMissingSlip44CoinType?: boolean } = {}
) => {
  if (!asset.blockchainConfiguration) {
    throw new Error(`Blockchain configuration is required for ${asset.canonicalName}`);
  }
  if (asset.assetType?.id !== CryptoAssetType.Native && asset.assetType?.id !== CryptoAssetType.Erc20) {
    throw new Error(`Asset type is required for ${asset.canonicalName}`);
  }
  if (!allowMissingSlip44CoinType && typeof asset.blockchainConfiguration.slip44CoinType !== 'number') {
    throw new Error(`SLIP-0044 coin type is required for ${asset.canonicalName}`);
  }
  if (isEvmAssetDTO(asset) && typeof asset.blockchainConfiguration.chainId !== 'number') {
    throw new Error(`EVM chain ID is required for ${asset.canonicalName}`);
  }
  if (isEvmAssetDTO(asset) && !asset.blockchainConfiguration.rpcUrl) {
    throw new Error(`EVM RPC URL is required for ${asset.canonicalName}`);
  }
  if (isErc20AssetDTO(asset) && !asset.definitionAddress) {
    throw new Error(`Definition address is required for ${buildUniqueKey(asset)}`);
  }
  if (typeof asset.decimalPrecision !== 'number') {
    throw new Error(`Decimal precision is required for ${buildUniqueKey(asset)}`);
  }
};

const validateCryptoAssetDTORelationships = (asset: CryptoAssetDTO, assets: CryptoAssetDTO[]) => {
  if (!findFeeAsset(asset, assets)) {
    throw new Error(`Fee CryptoAsset not found for ${buildUniqueKey(asset)}`);
  }
};

const discardInvalidCryptoAssetDTO = (asset: CryptoAssetDTO, error: unknown, stage: string) => {
  console.error('Invalid CryptoAsset discarded', { asset, error, stage });
};

const filterValidCryptoAssetDTOs = (
  assets: CryptoAssetDTO[],
  validate: (asset: CryptoAssetDTO) => void,
  stage: string
) =>
  assets.filter((asset) => {
    try {
      validate(asset);
      return true;
    } catch (error) {
      discardInvalidCryptoAssetDTO(asset, error, stage);
      return false;
    }
  });

const tryCreateCryptoAsset = (asset: CryptoAssetDTO, assets: CryptoAssetDTO[]) => {
  try {
    return createCryptoAsset(asset, assets);
  } catch (error) {
    discardInvalidCryptoAssetDTO(asset, error, 'createCryptoAsset');
    return null;
  }
};

const buildRoutingKey = (asset: CryptoAssetDTO): CryptoAssetRoutingKey => {
  const slip44CoinType = asset.blockchainConfiguration.slip44CoinType;
  const assetType = asset.assetType.id;
  const assetTypeName = encodeURIComponent(asset.assetType.name.toLowerCase());
  const chainId = asset.blockchainConfiguration.chainId ?? '_';
  return {
    assetType,
    chainId,
    slip44CoinType,
    value: `slip44-${slip44CoinType}.chain-${chainId}.${assetTypeName}`,
  };
};

const buildUniqueKey = (asset: CryptoAssetDTO) => {
  const canonicalName = encodeURIComponent(asset.canonicalName);
  const routingKey = buildRoutingKey(asset).value;
  return `${routingKey}.${canonicalName}`;
};

const findFeeAsset = (asset: CryptoAssetDTO, assets: CryptoAssetDTO[]) => {
  if (isNativeAssetDTO(asset) || isBitcoinAssetDTO(asset)) {
    return asset;
  }
  const chainId = asset.blockchainConfiguration.chainId;
  return assets.find(
    (candidate) => isNativeAssetDTO(candidate) && candidate.blockchainConfiguration.chainId === chainId
  );
};

const applyCryptoAssetRoutingDefaults = (asset: CryptoAssetDTO) => {
  const canonicalName = normalizeSymbol(asset.canonicalName);
  const slip44CoinType =
    asset.blockchainConfiguration.slip44CoinType ??
    (canonicalName === 'BTC' ? getBitcoinSlip44CoinType() : SLIP44.ETHEREUM);

  return {
    ...asset,
    blockchainConfiguration: {
      ...asset.blockchainConfiguration,
      blockExplorerApiUrl: resolveProxiedBlockExplorerApiUrl(asset),
      slip44CoinType,
      rpcUrl: resolveProxiedRpcUrl(asset),
    },
  };
};

const applyDefaultLayer1BlockchainFallback = (assets: CryptoAssetDTO[]) => {
  const defaultLayer1BlockchainKeys = new Set(assets.filter(isDefaultLayer1AssetDTO).map(getBlockchainKey));
  const ethNativeAsset = assets.find((asset) => {
    const isEth = normalizeSymbol(asset.canonicalName) === 'ETH';
    const isEthereum = asset.blockchainConfiguration.slip44CoinType === SLIP44.ETHEREUM;
    return isEth && isEthereum && isNativeAssetDTO(asset);
  });
  if (!defaultLayer1BlockchainKeys.size && ethNativeAsset) {
    defaultLayer1BlockchainKeys.add(getBlockchainKey(ethNativeAsset));
  }
  if (!defaultLayer1BlockchainKeys.size) {
    return assets;
  }

  return assets.map((asset) => ({
    ...asset,
    blockchainConfiguration: {
      ...asset.blockchainConfiguration,
      isDefaultLayer1:
        asset.blockchainConfiguration.isDefaultLayer1 || defaultLayer1BlockchainKeys.has(getBlockchainKey(asset)),
    },
  }));
};

const createShimmedBitcoinAsset = (): CryptoAssetDTO => ({
  id: -2203,
  canonicalName: 'BTC',
  displayName: 'Bitcoin',
  assetType: {
    id: CryptoAssetType.Native,
    name: 'Native',
  },
  definitionAddress: null,
  decimalPrecision: 8,
  blockchainConfiguration: {
    chainId: null,
    slip44CoinType: getBitcoinSlip44CoinType(),
    name: 'Bitcoin',
    rpcUrl: '',
    blockExplorerUrl: '',
    blockExplorerApiUrl: null,
    usesEIP1559: false,
    isDefaultLayer1: false,
    isDefaultLayer2: false,
  },
  settings: {
    allowPurchases: false,
    allowInApp: true,
    allowSell: false,
  },
});

const buildTraits = (asset: CryptoAssetDTO, assets: CryptoAssetDTO[]): CryptoAssetTraits => {
  const isBitcoin = isBitcoinAssetDTO(asset);
  const isNative = isNativeAssetDTO(asset);
  const isErc20 = isErc20AssetDTO(asset);
  const isDefaultLayer1 = isDefaultLayer1AssetDTO(asset);
  const isDefaultLayer2 = isDefaultLayer2AssetDTO(asset);
  const isDefaultLayer2Native = isDefaultLayer2 && isNative;
  const isDigitalReward = isDefaultLayer2;
  const isBrandErc20 =
    isDefaultLayer1 &&
    isErc20 &&
    assets.some((candidate) => {
      const isDifferentAsset = candidate.id !== asset.id;
      const hasSameCanonicalName = candidate.canonicalName === asset.canonicalName;
      return (
        isDifferentAsset && hasSameCanonicalName && isDefaultLayer2AssetDTO(candidate) && isNativeAssetDTO(candidate)
      );
    });

  return {
    isBitcoin,
    isBrandErc20,
    isDefaultLayer1,
    isDefaultLayer2,
    isDefaultLayer2Native,
    isDigitalReward,
    isErc20,
    isEvm: isEvmAssetDTO(asset),
    isNative,
  };
};

export const createCryptoAsset = (asset: CryptoAssetDTO, assets: CryptoAssetDTO[]): CryptoAsset => {
  validateCryptoAssetDTO(asset);
  validateCryptoAssetDTORelationships(asset, assets);
  const feeAsset = findFeeAsset(asset, assets) as CryptoAssetDTO;

  return {
    asset,
    display: {
      decimalPrecision: asset.decimalPrecision as number,
      name: asset.displayName,
      symbol: asset.canonicalName,
    },
    fee: {
      cryptoAssetUniqueKey: buildUniqueKey(feeAsset),
      decimalPrecision: feeAsset.decimalPrecision as number,
    },
    routingKey: buildRoutingKey(asset),
    traits: buildTraits(asset, assets),
    uniqueKey: buildUniqueKey(asset),
  };
};

export const createCryptoAssets = (cryptoAssetDTOs: CryptoAssetDTO[]): CryptoAsset[] => {
  const locallyValidAssets = filterValidCryptoAssetDTOs(
    cryptoAssetDTOs,
    (asset) => validateCryptoAssetDTO(asset, { allowMissingSlip44CoinType: true }),
    'pre-routing-defaults'
  );
  const assetsWithRoutingDefaults = locallyValidAssets.map((asset) => applyCryptoAssetRoutingDefaults(asset));
  const validAssetsWithRoutingDefaults = filterValidCryptoAssetDTOs(
    assetsWithRoutingDefaults,
    (asset) => validateCryptoAssetDTO(asset),
    'routing-defaults'
  );
  const normalizedAssets = applyDefaultLayer1BlockchainFallback(validAssetsWithRoutingDefaults);
  const hasBitcoinAsset = normalizedAssets.some(isBitcoinAssetDTO);
  const assetsWithBitcoinShim = hasBitcoinAsset ? normalizedAssets : [...normalizedAssets, createShimmedBitcoinAsset()];
  const assets = filterValidCryptoAssetDTOs(
    assetsWithBitcoinShim,
    (asset) => validateCryptoAssetDTORelationships(asset, assetsWithBitcoinShim),
    'relationships'
  );
  const cryptoAssets = assets
    .map((asset) => tryCreateCryptoAsset(asset, assets))
    .filter((asset): asset is CryptoAsset => Boolean(asset));
  console.info('CryptoAssets', cryptoAssets);

  return cryptoAssets;
};
